// The Cooking workflow line on a production (HD-8, from the retired components/VideoCard.jsx). Says which phase this card
// is and offers the cross-phase actions: from Phase 1, pre-assign the Phase 2 writer and "Write Phase 2 script" (creates
// the Phase 2 row and its TH script, POST /api/cooking/phase2), or open the existing Phase 2 card; from Phase 2, open
// Phase 1. Same calls as before: PATCH /api/live-production/:id { phase2PreassignedWriterId }, POST /api/cooking/phase2.
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, PenLine } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../ui/Button';
import Select from '../../ui/Select';
import StageChip from '../../ui/StageChip';
import { useToast } from '../../ui/Toast';
import { clean } from '../../shell/copy';
import { ladderOf } from './shared';

export default function CookingPhaseBanner({ record, members, user, onRecordUpdated }) {
  const navigate = useNavigate();
  const toast = useToast();
  const isPhase1 = record.cookingPhase === 1;
  const sibling = isPhase1 ? record.cookingPhase2 : record.cookingPhase1;
  const roles = user?.roles || [];
  const canWritePhase2 = ['Writer', 'Admin', 'Power User', 'Power_User'].some(r => roles.includes(r));
  const writerMembers = (members || []).filter(m => (m.roles || []).some(r => r === 'Writer' || r.role === 'Writer'));
  const [creating, setCreating] = useState(false);

  const handlePreassignWriter = useCallback(async (writerId) => {
    try {
      const r = await api.patch(`/api/live-production/${record.id}`, { phase2PreassignedWriterId: writerId || null });
      onRecordUpdated?.(r.data);
    } catch {
      toast('The Phase 2 writer did not change. Try again.', { tone: 'danger' });
    }
  }, [record.id, onRecordUpdated, toast]);

  const handleWritePhase2 = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/api/cooking/phase2', { phase1LiveProductionId: record.id });
      const newScriptId = r.data?.script?.id;
      if (newScriptId) navigate('/scripts', { state: { openScriptId: newScriptId, fromVideoCard: true } });
    } catch (err) {
      toast(clean(err?.response?.data?.error) || 'The Phase 2 script was not started. Try again.', { tone: 'danger' });
    } finally {
      setCreating(false);
    }
  }, [record.id, creating, navigate, toast]);

  const phaseLabel = isPhase1 ? 'Phase 1, cooking footage' : 'Phase 2, talking head';
  const siblingLabel = isPhase1 ? 'Phase 2, talking head' : 'Phase 1, cooking footage';

  return (
    <section className="ui-region ui-region--pad ui-cluster" aria-label="Cooking workflow" data-testid="cooking-banner">
      <div className="ui-stack ui-stack--tight ui-grow">
        <h2 className="ui-heading">Cooking: {phaseLabel}</h2>
        <span className="ui-meta">Cooking workflow, {clean(record.client?.name) || 'no client'}</span>
      </div>
      {sibling ? (
        <span className="ui-cluster">
          <span className="ui-dense ui-text-2">{siblingLabel}</span>
          <StageChip stage={ladderOf(sibling.status)} />
          <Button variant="secondary" icon={isPhase1 ? ExternalLink : ArrowLeft} onClick={() => navigate(`/production/${sibling.id}`)}>
            {isPhase1 ? 'Open Phase 2' : 'Open Phase 1'}
          </Button>
        </span>
      ) : isPhase1 ? (
        <span className="ui-cluster">
          {writerMembers.length > 0 && (
            <Select aria-label="Phase 2 writer" value={record.phase2PreassignedWriterId || ''} onChange={e => handlePreassignWriter(e.target.value)}
              title="Pre-assign the Phase 2 writer (used for the notification when Phase 1 is filmed)"
              options={[{ value: '', label: 'Phase 2 writer: the Phase 1 writer' }, ...writerMembers.map(m => ({ value: m.id, label: clean(m.name) }))]} />
          )}
          {canWritePhase2 && (
            <Button variant="secondary" icon={PenLine} onClick={handleWritePhase2} loading={creating} disabled={record.status !== 'Approved'}
              title={record.status !== 'Approved'
                ? `Phase 1 video must be Approved before Phase 2 writing can begin (currently ${record.status}).`
                : 'Create the Phase 2 TH script tied to this Cooking project'}>
              Write Phase 2 script
            </Button>
          )}
        </span>
      ) : null}
    </section>
  );
}
